import { useMutation, useQueryClient } from 'react-query';

import { FormikHelpers } from 'formik';
import { AllowanceBudgetType, AllowancePayload } from 'services/api';
import useApi from 'src/hooks/useApi';
import useNotification from 'src/hooks/useNotification';
import AllowanceQueryKeys from 'src/query-keys/allowance';

import { LoadingAnimation } from 'components/LoadingAnimation';

import { allowanceInitialValues } from './AddAllowanceForm';
import {
  AllowanceDetailForm,
  AllowanceValuesType
} from './AllowanceDetailForm';
import { AllowanceFormProps } from './types';

export const EditAllowanceForm = ({
  formRef,
  setAction,
  allowance,
  loadingAllowance,
  editable,
  onQuery,
  ...others
}: AllowanceFormProps) => {
  const api = useApi();
  const { pushNotification, dismissAllNotifications, dismissNotification } =
    useNotification();
  const queryClient = useQueryClient();

  const editAllowance = useMutation(
    (allowance: { id: number; payload: AllowancePayload }) =>
      api.company.allowance.update(allowance.id, allowance.payload)
  );

  const handleSubmitEdit = (
    values: AllowanceValuesType,
    actions: FormikHelpers<AllowanceValuesType>
  ) => {
    const snackbarKey = pushNotification(`Updating ${values.name}...`, {
      variant: 'info'
    });

    allowance &&
      editAllowance.mutate(
        {
          id: allowance.id,
          payload: values as AllowanceValuesType
        },
        {
          onSuccess: () => {
            queryClient
              .invalidateQueries(AllowanceQueryKeys.list())
              .then(() => {
                dismissNotification(snackbarKey);
                pushNotification(`${values.name} updated`, {
                  variant: 'success'
                });
                setAction(false);
                onQuery('');
              });
          },
          onError: (e) => {
            dismissNotification(snackbarKey);

            const error = e as Error;
            pushNotification(
              `Error updating ${values.name}, ${error.message}`,
              {
                variant: 'error',
                onClick: () => dismissAllNotifications()
              }
            );
          },
          onSettled: () => {
            actions.setSubmitting(false);
          }
        }
      );
  };

  if (loadingAllowance) return <LoadingAnimation />;

  const {
    name,
    budget,
    budget_type,
    allowance_date_from,
    allowance_date_to,
    applicable_weekdays,
    applicable_time_from,
    applicable_time_to,
    applicable_users,
    applicable_type,
    allow_flexible_locations,
    is_restaurant,
    is_shop,
    is_catering,
    is_delivery,
    is_pickup,
    expense_code_required_in_checkout,
    is_voucher,
    meals_included,
    state,
    company_departments,
    company_locations
  } = { ...allowanceInitialValues, ...allowance };

  return (
    <AllowanceDetailForm
      initialValues={{
        editable,
        name: name,
        budget: budget,
        input_budget: budget?.toString(),
        budget_type: budget_type as AllowanceBudgetType,
        allowance_date_from: allowance_date_from?.slice(
          0,
          allowance_date_from.lastIndexOf('T')
        ),
        allowance_date_to: allowance_date_to?.slice(
          0,
          allowance_date_from.lastIndexOf('T')
        ),
        applicable_weekdays: applicable_weekdays,
        applicable_all_day_time:
          applicable_time_from === '00:00' && applicable_time_to === '23:59',
        applicable_time_from: applicable_time_from,
        applicable_time_to: applicable_time_to,
        applicable_users: applicable_users || [],
        applicable_type: applicable_type,
        applicable_company_locations: company_locations.map((l) => l.id),
        ordering_rule_company_department: company_departments.map((d) => d.id),
        allow_flexible_locations: allow_flexible_locations,
        is_restaurant: is_restaurant,
        is_shop: is_shop,
        is_catering: is_catering,
        is_delivery: is_delivery,
        is_pickup: is_pickup,
        expense_code_required_in_checkout: expense_code_required_in_checkout,
        is_voucher: is_voucher,
        meals_included: meals_included,
        state: state,
        company_departments: company_departments,
        company_locations: company_locations
      }}
      handleSubmit={handleSubmitEdit}
      ref={formRef}
      onQuery={onQuery}
      {...others}
    />
  );
};
